<script setup>
  import { ref, computed, onMounted, onUnmounted, useTemplateRef } from 'vue'
  import { useFocusTrap } from '@/composables/useFocusTrap';

  const props = defineProps({
    button: {
      type: Object,
      require: true
    },
    positionX: {
      type: String,
      default: 'right'
    },
  })

  const showMenu = defineModel();

  const popup = useTemplateRef('popup');

  const focusTrap = useFocusTrap(popup);
  
  const state = ref('closed');
  const offsetPositions = ref({ left: 0, right: 0 });

  const offsetDirection = computed(() => {
    if (props.positionX === 'center') return 'center';

    if (offsetPositions.value.left < offsetPositions.value.right && offsetPositions.value.left <= 0) return 'left';
    if (offsetPositions.value.left > offsetPositions.value.right && offsetPositions.value.right <= 0) return 'right';

    return null;
  })  

  const offsetAmount = computed(() => {
    const offsetPosition = offsetPositions.value[offsetDirection.value];

    if (offsetDirection.value === 'center') return '-50%';
    if (!offsetDirection.value) return 0;

    return offsetDirection.value === 'left' ? `${Math.abs(offsetPosition)}px` : `${offsetPosition}px`;
  });

  function closeMenu() {
    state.value = 'closing';
    popup.value.addEventListener('animationend', () => {
      showMenu.value = false;
      state.value = 'closed';
    });
  }

  function setXPositions() {
    const leftPos = popup.value.getBoundingClientRect().left;
    const rightPos = popup.value.getBoundingClientRect().right;

    offsetPositions.value.left = (offsetPositions.value.left + leftPos) === 0 
                                  ? offsetPositions.value.left 
                                  : leftPos;

    offsetPositions.value.right = (rightPos - offsetPositions.value.right) === 0 
                                    ? offsetPositions.value.right
                                    : window.innerWidth - rightPos;
  }

  const handleBodyClick = (event) => {
    if (popup.value && !popup.value.contains(event.target) && !props.button.contains(event.target)) {
      closeMenu();
    }
  }
  
  onMounted(() => {
    if (!props.positionX === 'center') setXPositions();

    state.value = 'opening';

    popup.value.addEventListener('animationend', () => {
      state.value = 'open';
      focusTrap.activate();
    });

    document.body.addEventListener('click', handleBodyClick);
  });

  onUnmounted(() => {
    document.body.removeEventListener('click', handleBodyClick);
  });
</script>

<template>
  <div
    ref="popup"
    tabindex="-1"
    class="jwx-popup__menu"
    :style="`--position-x-offset: ${offsetAmount};`"
    :data-state="state"
    :data-offset="offsetDirection"
  >
    <slot></slot>
  </div>
</template>